// ImageLightbox — full-surface overlay that renders the original
// image at up to 90% × 90% of the surface with a close affordance and
// a download link.
//
// Portal-rendered through `usePortalRoot()` so the lightbox renders
// inside the embed's bounding box when wrapped in `<EmbeddableChat>`,
// and against `document.body` for the standalone shell. Escape
// closes. Clicking the scrim closes; clicks on the image / buttons do
// not. Focus is captured on open and restored on close.

import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { isEmbeddedPortal } from "~/lib/portal-positioning";
import { sanitizeBlobDownloadUrl } from "~/lib/safe-url";
import { ICON_STROKE } from "~/lib/icon";

export interface ImageLightboxProps {
  /** Fully-resolved source — `mxcToHttp(mxc)` with no thumbnail opts. */
  src: string;
  /** Accessible alt / caption. */
  alt: string;
  /** Optional download URL (usually identical to `src`). When present
   *  the toolbar renders a download button. */
  downloadUrl?: string;
  /** Download filename attribute for the anchor. */
  downloadName?: string;
  onClose: () => void;
}

export function ImageLightbox({
  src,
  alt,
  downloadUrl,
  downloadName,
  onClose,
}: ImageLightboxProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const portalRoot = usePortalRoot();
  const [failed, setFailed] = useState(false);
  // Defence in depth against anything except our own same-origin blob URL.
  // `undefined` hides the download button.
  const safeDownload = downloadUrl
    ? (sanitizeBlobDownloadUrl(downloadUrl) ?? undefined)
    : undefined;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Focus-management: capture on open, restore on close. In standalone
  // mode we lock `document.body` scroll so background content doesn't
  // move when the mousewheel scrolls over the scrim. In embedded mode
  // we deliberately do NOT touch `document.body` — mutating the host
  // page's `overflow` is a style-isolation violation, full stop. (Note
  // that `overflow: hidden` on the embed root only stops the embed
  // from scrolling itself; wheel events over the scrim still bubble
  // out of the embed and reach the host page. Freezing host scroll
  // while a lightbox is open inside an embed is a behaviour change
  // worth its own ticket — the obvious approach is a non-passive
  // `onWheel` preventDefault on the scrim, but that's out of scope
  // here.)
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const embedded = isEmbeddedPortal(portalRoot);
    const previousOverflow = document.body.style.overflow;
    if (!embedded) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      if (!embedded) {
        document.body.style.overflow = previousOverflow;
      }
      previouslyFocusedRef.current?.focus?.();
    };
  }, [portalRoot]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
        return;
      }
      // Tab / Shift+Tab cycle focus inside the dialog so keyboard
      // users can't escape the modal and tab into the timeline
      // behind the scrim. Mirrors the pattern in
      // `DeleteConfirmDialog`.
      if (e.key !== "Tab") return;
      const node = dialogRef.current;
      if (!node) return;
      const focusable = node.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [handleClose]);

  const isEmbedded = isEmbeddedPortal(portalRoot);
  const overlay = (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={alt ? `Image: ${alt}` : "Image"}
      onClick={handleClose}
      style={{
        position: isEmbedded ? "absolute" : "fixed",
        inset: 0,
        zIndex: 200,
        background: "var(--chat-lightbox-scrim)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--chat-space-6)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: "var(--chat-space-4)",
          right: "var(--chat-space-4)",
          display: "flex",
          gap: "var(--chat-lightbox-button-gap)",
          alignItems: "center",
        }}
      >
        {safeDownload ? (
          <a
            href={safeDownload}
            download={downloadName ?? ""}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download image"
            className="chat-lightbox-button chat-button"
            style={buttonStyle}
          >
            <DownloadGlyph />
          </a>
        ) : null}
        <button
          ref={closeRef}
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="chat-lightbox-button chat-button"
          style={{ ...buttonStyle, border: "none" }}
        >
          <CloseGlyph />
        </button>
      </div>
      {failed ? (
        <p
          role="status"
          style={{
            color: "var(--chat-fg-on-accent)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          Image unavailable.
        </p>
      ) : (
        <img
          src={src}
          alt={alt}
          onClick={(e) => e.stopPropagation()}
          onError={() => setFailed(true)}
          style={{
            // The chat tokens express the lightbox image cap as 90vw /
            // 90vh — fine for the standalone shell where the lightbox
            // covers the viewport, but wrong for an embed mounted in
            // a 600px-wide host slot: a wide image would size itself
            // against the host viewport and then get clipped by the
            // embed's `overflow: hidden`. In embed mode we cap relative
            // to the surface (90% of the embed) so the image fits
            // inside it.
            maxWidth: isEmbedded ? "90%" : "var(--chat-lightbox-image-max-width)",
            maxHeight: isEmbedded ? "90%" : "var(--chat-lightbox-image-max-height)",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            borderRadius: "var(--chat-radius-md)",
            background: "transparent",
          }}
        />
      )}
    </div>
  );

  if (!portalRoot) return null;
  return createPortal(overlay, portalRoot);
}

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "var(--chat-lightbox-button-size)",
  height: "var(--chat-lightbox-button-size)",
  borderRadius: "var(--chat-radius-full)",
  background: "rgba(255, 255, 255, 0.12)",
  color: "rgb(255, 255, 255)",
  border: "none",
  cursor: "pointer",
  textDecoration: "none",
};

function CloseGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function DownloadGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/** Lightbox state for a surface that shows images: open with a blob URL, which is revoked on close. */
export function useImageLightbox(): {
  openImage: (src: string, alt: string, name: string) => void;
  lightbox: ReactNode;
} {
  const [shown, setShown] = useState<{ src: string; alt: string; name: string } | null>(null);
  const openImage = useCallback((src: string, alt: string, name: string) => {
    setShown({ src, alt, name });
  }, []);
  const close = useCallback(() => setShown(null), []);
  useEffect(() => {
    return () => {
      if (shown?.src.startsWith("blob:")) URL.revokeObjectURL(shown.src);
    };
  }, [shown]);
  const lightbox = shown ? (
    <ImageLightbox
      src={shown.src}
      alt={shown.alt}
      downloadUrl={shown.src}
      downloadName={shown.name}
      onClose={close}
    />
  ) : null;
  return { openImage, lightbox };
}
